// src/Pages/Trips.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

// ✅ callable Functions for guest cancel
import { getFunctions, httpsCallable } from "firebase/functions";

import { createOrGetInquiryThread as maybeCreateThread } from "../firebase";

/* ------------------------------- Utilities ------------------------------- */
const currency = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(
    n ?? 0
  );

const pad2 = (n) => String(n).padStart(2, "0");

const parseYYYYMMDD = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(s || ""))) return null;
  const [Y, M, D] = s.split("-").map(Number);
  return new Date(Y, M - 1, D);
};

const toYYYYMMDD = (d) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const toSerial = (s) => {
  const d = parseYYYYMMDD(s);
  if (!d) return null;
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
};

const todaySerial = () => {
  const d = new Date();
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
};

const fmtDate = (s) => {
  const d = parseYYYYMMDD(s);
  return d
    ? d.toLocaleDateString(undefined, {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";
};

const fmtRange = (start, endExclusive) => {
  const s = parseYYYYMMDD(start);
  const eEx = parseYYYYMMDD(endExclusive);
  if (!s || !eEx) return "—";
  const eIncl = new Date(eEx);
  eIncl.setDate(eIncl.getDate() - 1);
  const sameYear = s.getFullYear() === eIncl.getFullYear();
  const optsStart = {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  };
  const optsEnd = { month: "short", day: "numeric", year: "numeric" };
  return `${s.toLocaleDateString(
    undefined,
    optsStart
  )}–${eIncl.toLocaleDateString(undefined, optsEnd)}`;
};

const nightsBetween = (start, endExclusive) => {
  const s = parseYYYYMMDD(start);
  const eEx = parseYYYYMMDD(endExclusive);
  if (!s || !eEx) return 0;
  return Math.max(0, Math.round((eEx - s) / 86400000));
};

/* ---------------------------- Constants / guards ---------------------------- */
const PER_DAY_ID_RE = /_\d{4}-\d{2}-\d{2}$/;

const PAID_STATUSES = new Set([
  "paid",
  "succeeded",
  "complete",
  "completed",
  "confirmed",
]);
const isPaid = (status) =>
  PAID_STATUSES.has(String(status || "").toLowerCase());

/* ------------------------- Review categories / helpers ------------------------- */
const DAY_CATEGORIES = [
  { key: "cleanliness", label: "Cleanliness" },
  { key: "accuracy", label: "Accuracy of listing" },
  { key: "host", label: "Host communication" },
  { key: "convenience", label: "Convenience / parking" },
  { key: "value", label: "Value" },
];

const OVERNIGHT_CATEGORIES = [
  { key: "cleanliness", label: "Cleanliness" },
  { key: "accuracy", label: "Accuracy of listing" },
  { key: "communication", label: "Host communication" },
  { key: "comfort", label: "Comfort / sleep quality" },
  { key: "location", label: "Location" },
  { key: "value", label: "Value" },
];

const clamp1to5 = (n) => Math.max(1, Math.min(5, Number(n) || 1));
const average = (arr) => {
  const nums = arr.map(Number).filter((n) => Number.isFinite(n));
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
};
const starLabel = (n) =>
  ({ 1: "Terrible", 2: "Poor", 3: "Okay", 4: "Great", 5: "Excellent" }[n] ||
  "");

/* --------------------------------- Small UI -------------------------------- */
function StarRating({ value, onChange, readOnly = false, sizePx = 28 }) {
  const [hover, setHover] = useState(0);
  const active = hover || value || 0;

  return (
    <div
      className="inline-flex items-center gap-1"
      role="radiogroup"
      aria-label="Star rating"
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = active >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} ${n > 1 ? "stars" : "star"}${
              filled ? " selected" : ""
            }`}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onFocus={() => !readOnly && setHover(n)}
            onBlur={() => !readOnly && setHover(0)}
            onKeyDown={(e) => {
              if (readOnly) return;
              if (e.key === "ArrowRight" || e.key === "ArrowUp") {
                e.preventDefault();
                onChange?.(clamp1to5((value || 0) + 1));
              } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
                e.preventDefault();
                onChange?.(clamp1to5((value || 0) - 1));
              } else if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                onChange?.(n);
              }
            }}
            onClick={() => !readOnly && onChange?.(n)}
            className={`flex items-center justify-center rounded-md transition
                        ${
                          readOnly
                            ? "cursor-default"
                            : "cursor-pointer hover:scale-105 active:scale-95"
                        }`}
            style={{ width: sizePx, height: sizePx, lineHeight: 1 }}
          >
            <span className="text-2xl leading-none select-none">
              {filled ? "★" : "☆"}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------- Review helpers ------------------------------ */
function determineEndSerial(b) {
  const isOvernight =
    (b?.mode || "").toLowerCase() === "overnight" ||
    (b?.startDate && (b?.endDateExclusive || b?.endDate));
  if (isOvernight) return toSerial(b?.endDateExclusive || b?.endDate || "");
  const start = b?.date || b?.startDate || "";
  const d = parseYYYYMMDD(start);
  if (!d) return null;
  const next = new Date(d);
  next.setDate(next.getDate() + 1);
  return toSerial(toYYYYMMDD(next));
}

/* --------------------------- Day vs Overnight helper --------------------------- */
const normalizeWindow = (b) => {
  const isOvernight =
    (b.mode || "").toLowerCase() === "overnight" ||
    (b.startDate && (b.endDateExclusive || b.endDate));

  if (isOvernight) {
    const start = b.startDate || null;
    const endEx = b.endDateExclusive || b.endDate || null;
    return {
      isOvernight: true,
      start,
      endExclusive: endEx,
      startSerial: toSerial(start),
      endSerialExclusive: toSerial(endEx),
    };
  }
  // Day use → model as [date, date+1)
  const start = b.date || b.startDate || null;
  let endExclusive = null;
  if (start) {
    const d = parseYYYYMMDD(start);
    const next = new Date(d);
    next.setDate(next.getDate() + 1);
    endExclusive = toYYYYMMDD(next);
  }
  return {
    isOvernight: false,
    start,
    endExclusive,
    startSerial: toSerial(start),
    endSerialExclusive: toSerial(endExclusive),
  };
};

/* -------------------------------- Badges -------------------------------- */
const statusBadge = (status) => {
  const base =
    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border";
  switch ((status || "").toLowerCase()) {
    case "paid":
    case "succeeded":
    case "complete":
    case "completed":
    case "confirmed":
      return (
        <span
          className={`${base} border-green-200 text-green-700 bg-green-50`}
        >
          Paid
        </span>
      );
    case "pending":
      return (
        <span
          className={`${base} border-amber-200 text-amber-700 bg-amber-50`}
        >
          Pending
        </span>
      );
    case "authorized":
      return (
        <span className={`${base} border-blue-200 text-blue-700 bg-blue-50`}>
          Authorized
        </span>
      );
    case "canceled":
      return (
        <span
          className={`${base} border-neutral-200 text-neutral-600 bg-neutral-50`}
        >
          Canceled
        </span>
      );
    case "refunded":
      return (
        <span className={`${base} border-blue-200 text-blue-700 bg-blue-50`}>
          Refunded
        </span>
      );
    default:
      return (
        <span
          className={`${base} border-neutral-200 text-neutral-600 bg-neutral-50`}
        >
          {status || "—"}
        </span>
      );
  }
};

/* ------------------------------- Review Modal ------------------------------ */
function ReviewModal({
  open,
  onClose,
  booking,
  listing,
  me,
  existing,
  onSaved,
}) {
  const isOvernight =
    (booking?.mode || "").toLowerCase() === "overnight" ||
    (booking?.startDate && (booking?.endDateExclusive || booking?.endDate));
  const cats = isOvernight ? OVERNIGHT_CATEGORIES : DAY_CATEGORIES;

  const [overall, setOverall] = useState(existing?.overall || 5);
  const [overallHover, setOverallHover] = useState(0);
  const [catRatings, setCatRatings] = useState(() => {
    const seed = {};
    for (const c of cats) seed[c.key] = existing?.categories?.[c.key] || 5;
    return seed;
  });
  const [headline, setHeadline] = useState(existing?.headline || "");
  const [body, setBody] = useState(existing?.body || "");
  const [privateNote, setPrivateNote] = useState(existing?.privateNote || "");
  const [submitting, setSubmitting] = useState(false);

  // Reset values when opening
  useEffect(() => {
    if (!open) return;
    const seed = {};
    for (const c of cats) seed[c.key] = existing?.categories?.[c.key] || 5;
    setOverall(existing?.overall || 5);
    setOverallHover(0);
    setCatRatings(seed);
    setHeadline(existing?.headline || "");
    setBody(existing?.body || "");
    setPrivateNote(existing?.privateNote || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, booking?.id]);

  // Lock background when modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const displayOverall = overallHover || overall;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!me?.uid || !booking?.id) return;

    setSubmitting(true);
    try {
      const db = getFirestore();
      const reviewId = `${booking.id}_${me.uid}`;
      const ref = doc(db, "reviews", reviewId);

      const catVals = Object.values(catRatings).map((n) => clamp1to5(n));
      const computedOverall =
        Number(overall) >= 1 && Number(overall) <= 5
          ? overall
          : Math.round(average(catVals));

      const payload = {
        id: reviewId,
        bookingId: booking.id,
        listingId: String(booking.listingId || listing?.id || ""),
        hostUid:
          listing?.hostUid ||
          listing?.ownerUid ||
          listing?.uid ||
          listing?.userId ||
          booking?.hostUid ||
          "",
        guestUid: me.uid,
        mode: isOvernight ? "overnight" : "day",
        categories: catRatings,
        overall: clamp1to5(computedOverall),
        headline: String(headline || "").slice(0, 140),
        body: String(body || "").slice(0, 4000),
        privateNote: String(privateNote || "").slice(0, 4000),
        bookingEndSerial: determineEndSerial(booking),
        createdAt: existing?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        listingTitle: listing?.title || "",
        listingCity: listing?.city || "",
        listingState: listing?.stateCode || listing?.state || "",
        guestDisplayName: me.displayName || me.email || "Guest",
        guestPhotoURL: me.photoURL || "",
      };

      await setDoc(ref, payload, { merge: true });
      onSaved?.(payload);
      onClose?.();
    } catch (err) {
      console.error("[ReviewModal] submit error:", err);
      alert("We couldn’t save your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Sheet / Dialog container (scroll-safe) */}
      <div
        className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-[0_20px_70px_-28px_rgba(0,0,0,0.45)] overflow-hidden flex"
        style={{
          maxHeight: "100svh",
        }}
      >
        <form
          className="flex h-full w-full flex-col min-h-0"
          onSubmit={handleSubmit}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 border-b border-neutral-200 p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  Review
                </div>
                <div className="truncate text-xl font-semibold">
                  {listing?.title || "Your stay"}{" "}
                  <span className="text-neutral-500">
                    ({isOvernight ? "Overnight" : "Day use"})
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-neutral-200 px-3 py-1 text-sm hover:bg-neutral-50"
              >
                Close
              </button>
            </div>
          </div>

          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6 pb-40"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              scrollbarGutter: "stable both-edges",
            }}
          >
            <section>
              <div className="mb-1 text-sm text-neutral-600">Overall</div>
              <div className="flex flex-wrap items-center gap-3">
                <StarRating
                  value={overall}
                  onChange={setOverall}
                  readOnly={false}
                  sizePx={32}
                />
                <span className="text-sm text-neutral-600">
                  {starLabel(displayOverall) || "Rate your stay"}
                </span>
              </div>
            </section>

            <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {cats.map((c) => (
                <div
                  key={c.key}
                  className="rounded-xl border border-neutral-200 p-3 transition hover:shadow-sm"
                >
                  <div className="mb-1 text-sm font-medium text-neutral-800">
                    {c.label}
                  </div>
                  <StarRating
                    value={catRatings[c.key]}
                    onChange={(n) =>
                      setCatRatings((m) => ({ ...m, [c.key]: n }))
                    }
                    readOnly={false}
                    sizePx={28}
                  />
                </div>
              ))}
            </section>

            <section className="mt-6 grid gap-3">
              <div>
                <label className="mb-1 block text-sm text-neutral-600">
                  Headline (optional)
                </label>
                <input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="What stood out most?"
                  maxLength={140}
                  className="w-full rounded-xl border border-neutral-300 px-3 py-2 outline-none focus:border-neutral-800"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-neutral-600">
                  Your review
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Share details that would help future guests."
                  rows={6}
                  maxLength={4000}
                  className="w-full rounded-xl border border-neutral-300 px-3 py-2 outline-none focus:border-neutral-800"
                />
              </div>
            </section>

            <section className="mt-6">
              <label className="mb-1 block text-sm text-neutral-600">
                Private note to host (optional)
              </label>
              <textarea
                value={privateNote}
                onChange={(e) => setPrivateNote(e.target.value)}
                rows={3}
                maxLength={4000}
                placeholder="Only the host sees this. Not shown to other guests."
                className="w-full rounded-xl border border-neutral-300 px-3 py-2 outline-none focus:border-neutral-800"
              />
            </section>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-neutral-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-neutral-500">
                By submitting, you agree to our review policy. You can edit your
                review later.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center rounded-full bg-black px-5 py-2 text-sm font-semibold text-white transition hover:bg-neutral-800 disabled:opacity-60"
                style={{
                  paddingBottom:
                    "calc(0.5rem + env(safe-area-inset-bottom))",
                }}
              >
                {submitting
                  ? "Saving…"
                  : existing
                  ? "Update review"
                  : "Submit review"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

/* --------------------------- Check-in / Access Modal --------------------------- */
/**
 * IMPORTANT:
 * - We PREFER booking-level fields (booking.*) so hosts can customize per trip.
 * - If those are missing, we FALL BACK to listing.access (set in HostAccess).
 * - Sensitive secrets (codes, Wi-Fi, detailed parking) are only shown for
 *   fully paid / confirmed bookings (isPaid(status) === true).
 */
function AccessModal({ open, onClose, booking, listing }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  // Lock background when modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || !booking || !mounted) return null;

  const w = normalizeWindow(booking);
  const isOvernight =
    (booking.mode || "").toLowerCase() === "overnight" || w.isOvernight;

  const listingAccess = listing?.access || {};

  const status = booking.status || "";
  const isPaidStatus = isPaid(status);
  const canShowSecrets = isPaidStatus;

  // Merge booking-level fields with listing.access fallback
  const access = {
    summary:
      booking.accessSummary ||
      booking.checkInSummary ||
      listingAccess.accessSteps ||
      "",
    guide:
      booking.checkInGuide ||
      booking.accessInstructions ||
      listingAccess.accessSteps ||
      "",
    doorCode: booking.doorCode || listingAccess.doorCode || "",
    gateCode: booking.gateCode || listingAccess.gateCode || "",
    garageCode: booking.garageCode || listingAccess.garageCode || "",
    wifiNetwork: booking.wifiNetwork || listingAccess.wifiName || "",
    wifiPassword: booking.wifiPassword || listingAccess.wifiPassword || "",
    parkingNote:
      booking.parkingNote || listingAccess.parkingInstructions || "",
    checkInFrom:
      booking.checkInFrom ||
      listingAccess.checkInFrom ||
      listing?.checkInFrom ||
      "",
    checkInTo:
      booking.checkInTo ||
      listingAccess.checkInTo ||
      listing?.checkInTo ||
      "",
    checkOutBy:
      booking.checkOutBy ||
      listingAccess.checkOutBy ||
      listing?.checkOutBy ||
      "",
  };

  const hasAnyAccess =
    !!(
      access.summary ||
      access.guide ||
      access.doorCode ||
      access.gateCode ||
      access.garageCode ||
      access.wifiNetwork ||
      access.wifiPassword ||
      access.parkingNote
    );

  const dateLine = isOvernight
    ? `${fmtRange(w.start, w.endExclusive)} • ${nightsBetween(
        w.start,
        w.endExclusive
      )} night${nightsBetween(w.start, w.endExclusive) === 1 ? "" : "s"}`
    : fmtDate(booking.date || w.start);

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-[0_20px_70px_-28px_rgba(0,0,0,0.45)] overflow-hidden flex"
        style={{ maxHeight: "100svh" }}
      >
        <div
          className="flex h-full w-full flex-col min-h-0"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="shrink-0 border-b border-neutral-200 p-4 sm:p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  Check-in & access
                </div>
                <div className="truncate text-xl font-semibold">
                  {listing?.title || "Your booking"}
                </div>
                <div className="mt-1 text-xs text-neutral-600">{dateLine}</div>
                {(access.checkInFrom ||
                  access.checkInTo ||
                  access.checkOutBy) && (
                  <div className="mt-2 text-xs text-neutral-600">
                    {access.checkInFrom || access.checkInTo ? (
                      <div>
                        Check-in window:{" "}
                        <span className="font-medium text-neutral-800">
                          {[
                            access.checkInFrom &&
                              `from ${access.checkInFrom}`,
                            access.checkInTo && `until ${access.checkInTo}`,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        </span>
                      </div>
                    ) : null}
                    {access.checkOutBy && (
                      <div>
                        Check-out by{" "}
                        <span className="font-medium text-neutral-800">
                          {access.checkOutBy}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-neutral-200 px-3 py-1 text-sm hover:bg-neutral-50"
              >
                Close
              </button>
            </div>
          </div>

          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6 pb-40"
            style={{
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
              scrollbarGutter: "stable both-edges",
            }}
          >
            <section className="mb-4 grid gap-3 text-sm sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <div className="rounded-2xl border border-neutral-200 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs uppercase tracking-wide text-neutral-500">
                    Booking status
                  </span>
                  {statusBadge(booking.status)}
                </div>
                <p className="mt-2 text-xs text-neutral-600">
                  Detailed access instructions are only shown for confirmed,
                  paid bookings.
                </p>
              </div>

              <div className="rounded-2xl border border-neutral-200 p-3">
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  Window
                </div>
                <p className="mt-1 text-sm text-neutral-800">{dateLine}</p>
                <p className="mt-1 text-xs text-neutral-600">
                  Exact arrival and departure times are set by your host. Check
                  your confirmation email or message your host if you need
                  clarification.
                </p>
              </div>
            </section>

            <section className="mt-4 rounded-2xl border border-neutral-200 p-4">
              <h3 className="text-sm font-semibold text-neutral-900">
                Access details
              </h3>

              {!canShowSecrets && (
                <p className="mt-2 text-sm text-neutral-600">
                  This trip is not fully paid or confirmed yet. You’ll see
                  check-in and access details here after payment succeeds. For
                  now, you can message your host with any questions.
                </p>
              )}

              {canShowSecrets && !hasAnyAccess && (
                <p className="mt-2 text-sm text-neutral-600">
                  Your host hasn’t added detailed check-in instructions yet.
                  You can message your host with any questions about how to
                  enter the space, parking, or Wi-Fi.
                </p>
              )}

              {canShowSecrets && hasAnyAccess && (
                <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                  {(access.summary || access.guide) && (
                    <div className="rounded-xl border border-neutral-200 p-3 sm:col-span-2">
                      <div className="text-xs uppercase tracking-wide text-neutral-500">
                        Step-by-step check-in
                      </div>
                      {access.summary && (
                        <p className="mt-1 text-neutral-800 whitespace-pre-wrap">
                          {access.summary}
                        </p>
                      )}
                      {access.guide && access.guide !== access.summary && (
                        <p className="mt-2 text-neutral-800 whitespace-pre-wrap">
                          {access.guide}
                        </p>
                      )}
                    </div>
                  )}

                  {(access.doorCode ||
                    access.gateCode ||
                    access.garageCode) && (
                    <div className="rounded-xl border border-neutral-200 p-3">
                      <div className="text-xs uppercase tracking-wide text-neutral-500">
                        Door / gate / garage
                      </div>
                      {access.doorCode && (
                        <p className="mt-1 font-semibold text-neutral-900">
                          Door code:{" "}
                          <span className="font-mono tracking-wider">
                            {access.doorCode}
                          </span>
                        </p>
                      )}
                      {access.gateCode && (
                        <p className="mt-1 text-neutral-800">
                          Gate code:{" "}
                          <span className="font-mono tracking-wider">
                            {access.gateCode}
                          </span>
                        </p>
                      )}
                      {access.garageCode && (
                        <p className="mt-1 text-neutral-800">
                          Garage / parking code:{" "}
                          <span className="font-mono tracking-wider">
                            {access.garageCode}
                          </span>
                        </p>
                      )}
                      <p className="mt-2 text-[11px] text-neutral-500">
                        For safety, codes are intended only for the dates of
                        this booking. Hosts are encouraged to change codes
                        between guests.
                      </p>
                    </div>
                  )}

                  {(access.wifiNetwork || access.wifiPassword) && (
                    <div className="rounded-xl border border-neutral-200 p-3">
                      <div className="text-xs uppercase tracking-wide text-neutral-500">
                        Wi-Fi
                      </div>
                      {access.wifiNetwork && (
                        <p className="mt-1 text-neutral-800">
                          Network:{" "}
                          <span className="font-mono">
                            {access.wifiNetwork}
                          </span>
                        </p>
                      )}
                      {access.wifiPassword && (
                        <p className="mt-1 text-neutral-800">
                          Password:{" "}
                          <span className="font-mono">
                            {access.wifiPassword}
                          </span>
                        </p>
                      )}
                    </div>
                  )}

                  {access.parkingNote && (
                    <div className="rounded-xl border border-neutral-200 p-3 sm:col-span-2">
                      <div className="text-xs uppercase tracking-wide text-neutral-500">
                        Parking
                      </div>
                      <p className="mt-1 text-neutral-800 whitespace-pre-wrap">
                        {access.parkingNote}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>

          <div className="sticky bottom-0 z-10 border-t border-neutral-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/70 p-4 sm:p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-neutral-500">
                Never share door codes or access instructions outside of You’re
                Welcome messages.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-4 py-1.5 text-sm font-semibold hover:bg-neutral-50"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modal, document.body) : modal;
}

/* ------------------------------ Page Component ------------------------------ */
export default function Trips() {
  const auth = getAuth();
  const me = auth.currentUser || null;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [listings, setListings] = useState({}); // id -> listing doc
  const [error, setError] = useState("");

  // Reviews keyed by bookingId
  const [reviewsByBooking, setReviewsByBooking] = useState({});

  // cancel in-flight state
  const [cancelingId, setCancelingId] = useState(null);

  // Prevent repeated listing fetches
  const fetchedListingIds = useRef(new Set());

  // Review modal
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewContext, setReviewContext] = useState({
    booking: null,
    listing: null,
  });

  // Access modal
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessContext, setAccessContext] = useState({
    booking: null,
    listing: null,
  });

  // callable helper (guest-side cancel)
  async function callGuestCancelBooking(bookingId) {
    const functions = getFunctions();
    const fn = httpsCallable(functions, "guestCancelBooking");
    return fn({ bookingId });
  }

  // Subscribe to user's bookings (canonical only), hydrate listings lazily
  useEffect(() => {
    if (!me) {
      setLoading(false);
      return;
    }
    const db = getFirestore();

    let unsub = () => {};
    let usingFallback = false;
    let canceled = false;

    async function hydrateListingsIfNeeded(canonical) {
      const need = new Set(
        canonical
          .map((b) => String(b.listingId || ""))
          .filter(
            (id) =>
              id && !fetchedListingIds.current.has(id) && !listings[id]
          )
      );
      if (need.size === 0) return;

      const newListings = {};
      await Promise.all(
        [...need].map(async (id) => {
          try {
            const lsnap = await getDoc(doc(db, "listings", id));
            if (lsnap.exists()) newListings[id] = { id: lsnap.id, ...lsnap.data() };
          } catch {
            // ignore individual listing fetch errors
          } finally {
            fetchedListingIds.current.add(id);
          }
        })
      );
      if (!canceled && Object.keys(newListings).length) {
        setListings((prev) => ({ ...prev, ...newListings }));
      }
    }

    function processSnapshot(snap) {
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const canonical = all.filter((b) => {
        const idOk = !PER_DAY_ID_RE.test(String(b.id || ""));
        const hasMode =
          typeof b.mode === "string" &&
          (b.mode === "day" || b.mode === "overnight");
        const notBlocked = (b.status || "").toLowerCase() !== "blocked";
        return notBlocked && (hasMode || idOk);
      });

      setBookings(canonical);
      hydrateListingsIfNeeded(canonical).finally(
        () => !canceled && setLoading(false)
      );
    }

    const primaryQueryRef = query(
      collection(db, "bookings"),
      where("guestUid", "==", me.uid),
      where("mode", "in", ["day", "overnight"])
    );
    const fallbackQueryRef = query(
      collection(db, "bookings"),
      where("guestUid", "==", me.uid)
    );

    function start(subscribeFallback = false) {
      const qRef = subscribeFallback ? fallbackQueryRef : primaryQueryRef;
      unsub = onSnapshot(
        qRef,
        (snap) => {
          usingFallback = subscribeFallback;
          processSnapshot(snap);
        },
        (err) => {
          console.error("[Trips] onSnapshot error:", err);
          if (
            !usingFallback &&
            (err?.code === "failed-precondition" ||
              /index/i.test(err?.message || ""))
          ) {
            usingFallback = true;
            unsub?.();
            start(true);
            return;
          }
          setError("We couldn’t load your trips. Please refresh.");
          setLoading(false);
        }
      );
    }

    start(false);

    return () => {
      canceled = true;
      unsub?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.uid]);

  // Single reviews listener for this guest
  useEffect(() => {
    if (!me?.uid) return;
    const db = getFirestore();
    const qRef = query(
      collection(db, "reviews"),
      where("guestUid", "==", me.uid)
    );

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const map = {};
        snap.docs.forEach((d) => {
          const r = { id: d.id, ...d.data() };
          if (r?.bookingId) map[r.bookingId] = r;
        });
        setReviewsByBooking(map);
      },
      (err) => console.error("[Trips] reviews onSnapshot error:", err)
    );

    return () => unsub?.();
  }, [me?.uid]);

  // Sort + group (client-side)
  const { upcoming, past } = useMemo(() => {
    const now = todaySerial();
    const withWindow = bookings.map((b) => ({ b, w: normalizeWindow(b) }));

    withWindow.sort(
      (a, c) => (c.w.startSerial ?? 0) - (a.w.startSerial ?? 0)
    );

    const upcomingArr = [];
    const pastArr = [];
    for (const { b, w } of withWindow) {
      if ((w.endSerialExclusive ?? -1) > now) upcomingArr.push(b);
      else pastArr.push(b);
    }
    return { upcoming: upcomingArr, past: pastArr };
  }, [bookings]);

  /* ---------------------- Review modal orchestration ---------------------- */
  function openReview(booking) {
    const l = listings[String(booking.listingId)] || {};
    setReviewContext({ booking, listing: l });
    setReviewOpen(true);
  }

  /* ---------------------- Access modal orchestration ---------------------- */
  function openAccess(booking) {
    const l = listings[String(booking.listingId)] || {};
    setAccessContext({ booking, listing: l });
    setAccessOpen(true);
  }

  /* ------------------------------ Refund preview (UI-only) ------------------------------ */
  function refundPreviewText(b) {
    // mirrors backend MVP policy:
    // >=48h: 100% subtotal refund
    // <48h and before start: 50% subtotal refund
    // after start: 0%
    const start = parseYYYYMMDD(b.startDate || b.date);
    if (!start) return "Refund depends on your cancellation window.";
    const hoursToStart = (start.getTime() - Date.now()) / (1000 * 60 * 60);
    if (hoursToStart >= 48)
      return "You should receive a 100% refund of the nightly price (fees may be non-refundable).";
    if (hoursToStart > 0)
      return "You should receive a 50% refund of the nightly price (fees may be non-refundable).";
    return "This booking is not refundable because the stay has started.";
  }

  /* --------------------------------- Cards -------------------------------- */
    const BookingCard = ({ b }) => {
    const l = listings[String(b.listingId)] || {};
    const photo =
      l?.photos?.[0] ||
      "https://images.unsplash.com/photo-1505692794403-34d4982f88aa?q=80&w=800&auto=format&fit=crop";
    const title = l?.title || "Space";
    const whereLine = [l?.city, l?.stateCode || l?.state]
      .filter(Boolean)
      .join(", ");
    const total =
      Number.isFinite(Number(b.amountTotalCents))
        ? Number(b.amountTotalCents) / 100
        : Number(b.amountTotal || 0);

    const hostUid =
      l?.hostUid ||
      l?.ownerUid ||
      l?.uid ||
      l?.userId ||
      l?.user?.uid ||
      b.hostUid;

    // Window + mode
    const w = normalizeWindow(b);
    const now = todaySerial();
    const isOngoing =
      (w.startSerial ?? 0) <= now && (w.endSerialExclusive ?? -1) > now;

    // Display date(s)
    const dateLine = w.isOvernight
      ? `${fmtRange(w.start, w.endExclusive)} • ${nightsBetween(
          w.start,
          w.endExclusive
        )} night${
          nightsBetween(w.start, w.endExclusive) === 1 ? "" : "s"
        }`
      : fmtDate(b.date || w.start);

    // ✅ Gate full address behind paid status
    const isPaidStatus = isPaid(b.status);

    const cityState = [l?.city, l?.stateCode || l?.state]
      .filter(Boolean)
      .join(", ");

    // Only show street address when booking is fully paid/confirmed.
    // Otherwise show just city/state.
    const locationLine =
      isPaidStatus && (l?.addressLine1 || l?.address)
        ? `${l.addressLine1 || l.address}${
            cityState ? `, ${cityState}` : ""
          }`
        : cityState || "";

    const mapsHref =
      isPaidStatus && locationLine
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
            locationLine
          )}`
        : null;

    async function messageHost() {
      if (!me || !l?.id || !hostUid) return navigate("/messages");
      try {
        if (typeof maybeCreateThread === "function") {
          const t = await maybeCreateThread({
            listingId: l.id,
            hostUid,
            guestUid: me.uid,
            bookingId: b.id,
            listingTitle: l.title,
            listingPhoto: l.photos?.[0],
          });
          navigate(`/messages?thread=${t.id}`);
        } else {
          navigate("/messages");
        }
      } catch {
        navigate("/messages");
      }
    }

    // Resume payment link for pending (supports day + overnight)
    const resumeHref =
      String(b.status || "").toLowerCase() === "pending"
        ? w.isOvernight
          ? `/checkout/${encodeURIComponent(
              b.listingId
            )}?mode=overnight&start=${encodeURIComponent(
              b.startDate || ""
            )}&end=${encodeURIComponent(
              b.endDateExclusive || b.endDate || ""
            )}&guests=${encodeURIComponent(b.guests || 1)}`
          : `/checkout/${encodeURIComponent(
              b.listingId
            )}?mode=day&date=${encodeURIComponent(
              b.date || b.startDate || ""
            )}&guests=${encodeURIComponent(b.guests || 1)}`
        : null;

    const detailsHref = `/booking-success?bid=${encodeURIComponent(b.id)}`;
    const existingReview = reviewsByBooking[b.id];

    // Review CTA only after stay ends AND booking is paid
    const isPast = (w.endSerialExclusive ?? -1) <= now;
    const canLeaveNew = isPast && isPaid(b.status);
    const canReview = existingReview ? true : canLeaveNew;

    // guest-cancel eligibility
    const statusLower = String(b.status || "").toLowerCase();
    const approvalLower = String(b.approvalStatus || "").toLowerCase();

    const isPendingApproval =
      b.requiresApproval === true &&
      approvalLower === "pending" &&
      (statusLower === "authorized" || statusLower === "pending");

    const isFuturePaid = isPaid(b.status) && (w.startSerial ?? 0) > now;

    const canGuestCancel = isPendingApproval || isFuturePaid;
    const cancelLabel = isPendingApproval ? "Cancel request" : "Cancel booking";

    async function handleGuestCancel() {
      if (!me?.uid || !b?.id) return;

      const msg = isPendingApproval
        ? "Cancel this request? Your payment hold will be released."
        : `Cancel this booking?\n\n${refundPreviewText(b)}`;

      const ok = window.confirm(msg);
      if (!ok) return;

      setCancelingId(b.id);
      try {
        await callGuestCancelBooking(b.id);
        alert(isPendingApproval ? "Request canceled." : "Booking canceled.");
      } catch (err) {
        console.error("[Trips] guestCancelBooking failed:", err);
        alert(
          err?.message ||
            "We couldn’t cancel this booking. Please try again."
        );
      } finally {
        setCancelingId(null);
      }
    }

    return (
      <div
        key={b.id}
        className="flex items-start gap-4 rounded-2xl border border-neutral-200 bg-white p-5 shadow-sm transition hover:shadow-[0_16px_50px_-22px_rgba(0,0,0,0.25)]"
      >
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-neutral-200">
          <img src={photo} alt={title} className="h-full w-full object-cover" />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-base font-semibold">{title}</div>
              <div className="truncate text-xs text-neutral-600">
                {whereLine || "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center justify-end gap-2">
                {statusBadge(b.status)}
                {isOngoing && (
                  <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-semibold text-indigo-700">
                    Ongoing
                  </span>
                )}
              </div>
              <div className="mt-1 truncate text-[11px] text-neutral-500">
                #{b.id}
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div>
              <div className="text-neutral-500">
                {w.isOvernight ? "Dates" : "Date"}
              </div>
              <div className="font-medium text-neutral-800">{dateLine}</div>
            </div>
            <div>
              <div className="text-neutral-500">Guests</div>
              <div className="font-medium text-neutral-800">
                {b.guests || "—"}
              </div>
            </div>
            <div>
              <div className="text-neutral-500">Total</div>
              <div className="font-medium text-neutral-800">
                {currency(total)}
              </div>
            </div>
            {locationLine && (
              <div className="sm:col-span-1">
                <div className="text-neutral-500">Location</div>
                <div className="truncate font-medium text-neutral-800">
                  {locationLine}
                  {mapsHref && (
                    <a
                      href={mapsHref}
                      target="_blank"
                      rel="noreferrer"
                      className="ml-1 underline"
                    >
                      (map)
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Review strip */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {existingReview ? (
              <div className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs">
                <StarRating
                  value={existingReview.overall}
                  readOnly
                  sizePx={20}
                />
                <span className="font-medium text-neutral-700">
                  {Number(existingReview.overall || 0).toFixed(1)}
                </span>
                <span className="text-neutral-500">Your review</span>
              </div>
            ) : (
              <div className="text-xs text-neutral-600">
                {canLeaveNew
                  ? "Not reviewed yet"
                  : "Review available after your paid stay"}
              </div>
            )}

            {canReview && (
              <button
                type="button"
                onClick={() => openReview(b)}
                className="inline-flex items-center justify-center rounded-full bg-black px-3 py-1.5 text-sm font-semibold text-white hover:bg-neutral-800 w-full sm:w-auto"
              >
                {existingReview ? "Edit review" : "Leave a review"}
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => openAccess(b)}
              className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-semibold hover:bg-neutral-50"
            >
              Check-in &amp; access
            </button>

            <Link
              to={detailsHref}
              state={{
                bookingId: b.id,
                listing: l,
                date: b.date || null,
                startDate: b.startDate || null,
                endDate: b.endDateExclusive || b.endDate || null,
                guests: b.guests,
                total,
                status: b.status,
              }}
              className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-semibold hover:bg-neutral-50"
            >
              View details
            </Link>

            <button
              type="button"
              onClick={messageHost}
              className="inline-flex items-center justify-center rounded-full bg-black px-3 py-1.5 text-sm font-semibold text-white hover:bg-neutral-800"
            >
              Message host
            </button>

            {resumeHref && (
              <Link
                to={resumeHref}
                className="inline-flex items-center justify-center rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-semibold text-amber-800 hover:bg-amber-100"
              >
                Complete payment
              </Link>
            )}

            {b.stripeReceiptUrl && (
              <a
                href={b.stripeReceiptUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-semibold hover:bg-neutral-50"
              >
                View receipt
              </a>
            )}

            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-3 py-1.5 text-sm font-semibold hover:bg-neutral-50"
            >
              Print
            </button>

            {/* Guest cancel button */}
            {canGuestCancel && (
              <button
                type="button"
                onClick={handleGuestCancel}
                disabled={cancelingId === b.id}
                className="inline-flex items-center justify-center rounded-full border border-red-300 bg-red-50 px-3 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
              >
                {cancelingId === b.id ? "Canceling…" : cancelLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* ------------------------------- Screens ------------------------------- */
  if (!me) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <header className="sticky top-0 z-40 w-full border-b border-neutral-200 bg-white/70 backdrop-blur">
          <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
            <Link to="/" className="flex items-center gap-3" aria-label="Go home">
              <picture>
                <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
                <img
                  src="/YoureWelcome Logo.png"
                  alt="You’re Welcome"
                  className="h-10 w-auto"
                />
              </picture>
            </Link>
          </nav>
        </header>

        <div className="mx-auto max-w-5xl px-4 py-10">
          <h1 className="text-2xl font-semibold">Your bookings</h1>
          <p className="mt-2 text-neutral-600">
            Sign in to view your upcoming and past bookings.
          </p>
          <div className="mt-6">
            <Link
              to="/signin"
              className="inline-flex rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-800"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <header className="sticky top-0 z-40 w-full border-b border-neutral-200 bg-white/70 backdrop-blur">
          <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
            <Link to="/" className="flex items-center gap-3" aria-label="Go home">
              <picture>
                <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
                <img
                  src="/YoureWelcome Logo.png"
                  alt="You’re Welcome"
                  className="h-10 w-auto"
                />
              </picture>
            </Link>
          </nav>
        </header>

        <div className="mx-auto max-w-5xl px-4 py-10">
          <h1 className="text-2xl font-semibold">Your bookings</h1>
          <div className="mt-8 space-y-3">
            {[...Array(3)].map((_, i) => (
              <div
                key={i}
                className="h-28 w-full animate-pulse rounded-2xl border border-neutral-200 bg-white"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <header className="sticky top-0 z-40 w-full border-b border-neutral-200 bg-white/70 backdrop-blur">
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="flex items-center gap-3" aria-label="Go home">
            <picture>
              <source srcSet="/YoureWelcome Logo.svg" type="image/svg+xml" />
              <img
                src="/YoureWelcome Logo.png"
                alt="You’re Welcome"
                className="h-10 w-auto"
              />
            </picture>
          </Link>
        </nav>
      </header>

      <div className="mx-auto max-w-5xl px-4 py-10">
        <h1 className="text-2xl font-semibold">Your bookings</h1>
        <p className="mt-2 text-neutral-600">
          Day and overnight bookings appear here. Ongoing stays are marked.
        </p>

        <section className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Upcoming</h2>
            {upcoming.length > 0 && (
              <div className="text-sm text-neutral-500">
                {upcoming.length} booking(s)
              </div>
            )}
          </div>

          {upcoming.length === 0 ? (
            <div className="rounded-2xl border border-neutral-200 p-6">
              <p className="text-neutral-700">No upcoming trips yet.</p>
              <div className="mt-4 flex gap-2">
                <Link
                  to="/explore"
                  className="inline-flex rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-800"
                >
                  Explore spaces
                </Link>
                <Link
                  to="/messages"
                  className="inline-flex rounded-full border border-neutral-300 px-4 py-2 text-sm font-semibold hover:bg-neutral-50"
                >
                  Go to Messages
                </Link>
              </div>
            </div>
          ) : (
            <div className="grid gap-3">
              {upcoming.map((b) => (
                <BookingCard key={b.id} b={b} />
              ))}
            </div>
          )}
        </section>

        <section className="mt-10">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold">Past</h2>
            {past.length > 0 && (
              <div className="text-sm text-neutral-500">
                {past.length} booking(s)
              </div>
            )}
          </div>

          {past.length === 0 ? (
            <div className="rounded-2xl border border-neutral-200 p-6">
              <p className="text-neutral-700">No past trips yet.</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {past.map((b) => (
                <BookingCard key={b.id} b={b} />
              ))}
            </div>
          )}
        </section>

        {error && (
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      <ReviewModal
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        booking={reviewContext.booking}
        listing={reviewContext.listing}
        me={me}
        existing={
          reviewContext.booking
            ? reviewsByBooking[reviewContext.booking.id]
            : null
        }
        onSaved={(payload) => {
          setReviewsByBooking((prev) => ({
            ...prev,
            [payload.bookingId]: payload,
          }));
        }}
      />

      <AccessModal
        open={accessOpen}
        onClose={() => setAccessOpen(false)}
        booking={accessContext.booking}
        listing={accessContext.listing}
      />
    </div>
  );
}

/*
UI-only + access changes:
- Keeps original Airbnb-style card layout with image, actions, reviews, cancel, etc.
- Adds AccessModal (Check-in & access) that pulls from booking.* then listing.access.
- Shows door codes / Wi-Fi / parking only for fully paid bookings.
- Access button added to each card; View details, Message host, cancel logic unchanged.
*/
